import React from 'react';
import numeral from 'numeral';
import qs from 'qs';
import retry from 'async-retry';
import fileDownload from 'js-file-download';
import { get, flatten, } from 'lodash';
import { format as formatDate, } from 'date-fns';

import env from './env';
import { computeProductValues, } from './shared/models/product';
import { isMercariReadyToShip, } from './shared/models/order';

export function fullPathWithParams(params, { pathname, search }) {
  const currentParams = qs.parse(decodeURI(search.slice(1)));
  const newParams = {
    ...currentParams,
    ...params
  };
  const newSearch = qs.stringify(newParams);
  return `${pathname}${newSearch ? `?${newSearch}` : ''}`;
};

export function yen(value) {
  return (
    <>
      <span className="mr-1">&yen;</span>
      <span>{numeral(value).format('0,0')}</span>
    </>
  );
}

export function ordersToRows (orders, productsById, tenantsById = {}, shopsById = {}) {
  return orders.map((order) => {
    const { items: _items, tenantId, shopId, inventoryType = 'fba', logispShippingRequest, } = order;
    const shipments = flatten([get(order, 'FulfillmentShipment.member', [])]);
    const items = _items.map((item) => {
      const product = get(productsById, [item.itemId]);
      const sku = get(product, 'sku') || item.sku;
      const amazonProduct = get(product, 'amazonProduct');
      const title = get(amazonProduct, 'AttributeSets.ItemAttributes.Title');
      const imageUrl = get(amazonProduct, 'AttributeSets.ItemAttributes.SmallImage.URL');
      const asin = get(amazonProduct, 'Identifiers.MarketplaceASIN.ASIN');
      const itemUrl = `https://www.amazon.co.jp/gp/offer-listing/${asin}`;
      return {
        ...item,
        product,
        sku,
        title,
        imageUrl,
        asin,
        itemUrl,
      };
    });
    const tenant = tenantsById[tenantId];
    const shop = shopsById[shopId];
    const [shippedAt, shipmentCarrier, trackingNumber, shippedDates] = ({
      fba: () => {
        const shippedAt = shipments.map(_ => _.ShippingDateTime != null ? formatDate(new Date(_.ShippingDateTime), 'yyyy/MM/dd') : '').join(',');
        const shipmentCarrier = shipments.map(_ => get(_, 'FulfillmentShipmentPackage.member.CarrierCode', '')).join(',');
        const trackingNumber = shipments.map(_ => get(_, 'FulfillmentShipmentPackage.member.TrackingNumber', '')).join(',');
        const shippedDates = shipments.map(_ => _.ShippingDateTime ? new Date(_.ShippingDateTime) : null).filter(_ => _ != null);
        return [shippedAt, shipmentCarrier, trackingNumber, shippedDates];
      },
      hanropluslogi: () => {
        const shippedAt = logispShippingRequest?.shippingItems?.map(_ => _.shippedOn != null ? formatDate(new Date(_.shippedOn), 'yyyy/MM/dd') : '').join(',');
        const shipmentCarrier = logispShippingRequest?.carrier;
        const trackingNumber = logispShippingRequest?.shippingItems?.map(_ => _.documentNumber).join(',');
        const shippedDates = logispShippingRequest?.shippingItems?.map(_ => _.shippedOn ? new Date(_.shippedOn) : null).filter(_ => _ != null) || [];
        return [shippedAt, shipmentCarrier, trackingNumber, shippedDates];
      },
    })[inventoryType]();
    const isReadyToShip = ({
      rakuten: order.source?.orderProgress === 300,
      mercari: isMercariReadyToShip(order),
      yahoo: ['3', '5'].includes(order.source?.Pay?.SettleStatus),
    })[order.mall];
    return {
      order,
      items,
      tenant,
      shop,
      shipments,
      shippedAt,
      shipmentCarrier,
      trackingNumber,
      shippedDates,
      isReadyToShip,
    };
  })
    .filter(_ => _.items.length > 0);
}

export function readFile(file, type = 'readAsText') {
  const reader = new FileReader();
  reader[type](file);
  return new Promise((resolve) => {
    reader.addEventListener('load', _ => resolve(_.target.result));
  });
};

export function highlightText(text, words, secondaryWords = []) {
  return secondaryWords.reduce((x, word) => 
    x.replace(new RegExp(word, 'g'), `<mark style="background-color: #ff9999;">${word}</mark>`),
    words.reduce((x, word) => 
      x.replace(new RegExp(word, 'g'), `<mark>${word}</mark>`), text ?? '')
  );
};

export const fetchPdf = async (html, pdfOptions = {}, launchOptions = {}) => {
  const url = `${env('CLOUD_FUNCTIONS_ENDPOINT')}/html2Pdf`;
  const body = JSON.stringify({
    html,
    pdfOptions: { format: 'a4', margin: { top: 60, bottom: 60 }, ...pdfOptions },
    launchOptions: { deviceScaleFactor: 2, ...launchOptions },
  });
  const headers = { 'Content-Type': 'application/json' };
  return retry(() => fetch(url, { method: 'POST', body, headers }).then(_ => _.blob()), { retries: 3 });
};

export const downloadPdfFromHtml = async (html, fileName, pdfOptions) => {
  const blob = await fetchPdf(html, pdfOptions);
  fileDownload(blob, fileName);
};

export const activateRichTextHtml = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const imageNodes = doc.querySelectorAll('img[alt]');
  Array.from(imageNodes).map((imageNode) => {
    if(imageNode.getAttribute('alt')?.startsWith('[video] ')) {
      const videoNode = document.createElement('video');
      videoNode.setAttribute('src', imageNode.getAttribute('alt').replace('[video] ', ''));
      const posterUrl = imageNode.getAttribute('src');
      if(!posterUrl.includes('movie_play')) videoNode.setAttribute('poster', posterUrl);
      videoNode.setAttribute('controls', '');
      imageNode.parentNode.appendChild(videoNode);
      imageNode.parentNode.removeChild(imageNode);
    } else {
      const aNode = document.createElement('a');
      aNode.setAttribute('href', imageNode.getAttribute('alt'));
      aNode.setAttribute('target', '_blank');
      imageNode.parentNode.appendChild(aNode);
      aNode.appendChild(imageNode);
      imageNode.removeAttribute('alt');
    }
  });
  const anchorNodes = doc.querySelectorAll('a[href]');
  Array.from(anchorNodes).map((anchorNode) => {
    anchorNode.setAttribute('target', '_blank');
  });
  return doc.body.innerHTML;
};
